/**
 * 3×3 rotation fitting for ARAP. Given a covariance matrix `S = Σ w·e_rest·e_curᵀ`,
 * {@link fitRotation} returns the rotation `R ∈ SO(3)` that maximises `trace(Rᵀ S)`
 * (equivalently the closest rotation to `S`), which is the ARAP local step.
 *
 * All matrices are row-major `Float64Array(9)`: `m[3*row + col]`. The implementation
 * is allocation-free (module-level scratch) — safe because JS is single-threaded and
 * these calls are synchronous and non-reentrant.
 */

const _A = new Float64Array(9); // working symmetric matrix (SᵀS)
const _V = new Float64Array(9); // eigenvectors (columns)
const _U = new Float64Array(9); // left singular vectors (columns)
const _sigma = new Float64Array(3);

const EPS = 1e-9;
const JACOBI_SWEEPS = 8;

function identity(m: Float64Array): void {
  m[0] = 1; m[1] = 0; m[2] = 0;
  m[3] = 0; m[4] = 1; m[5] = 0;
  m[6] = 0; m[7] = 0; m[8] = 1;
}

/** One Jacobi rotation zeroing the symmetric (p,q) off-diagonal of `A`, accumulated into `V`. */
function jacobiRotate(A: Float64Array, V: Float64Array, p: number, q: number): void {
  const apq = A[3 * p + q];
  if (Math.abs(apq) < EPS) return;
  const app = A[3 * p + p];
  const aqq = A[3 * q + q];
  const theta = (aqq - app) / (2 * apq);
  const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
  const c = 1 / Math.sqrt(t * t + 1);
  const s = t * c;

  for (let k = 0; k < 3; k++) {
    const akp = A[3 * k + p];
    const akq = A[3 * k + q];
    A[3 * k + p] = c * akp - s * akq;
    A[3 * k + q] = s * akp + c * akq;
  }
  for (let k = 0; k < 3; k++) {
    const apk = A[3 * p + k];
    const aqk = A[3 * q + k];
    A[3 * p + k] = c * apk - s * aqk;
    A[3 * q + k] = s * apk + c * aqk;
  }
  for (let k = 0; k < 3; k++) {
    const vkp = V[3 * k + p];
    const vkq = V[3 * k + q];
    V[3 * k + p] = c * vkp - s * vkq;
    V[3 * k + q] = s * vkp + c * vkq;
  }
}

/** Symmetric eigendecomposition of `A` (9) → eigenvectors in `V` (columns), eigenvalues in `out3`. */
function jacobiEigenSymmetric(A: Float64Array, V: Float64Array, out3: Float64Array): void {
  identity(V);
  for (let sweep = 0; sweep < JACOBI_SWEEPS; sweep++) {
    jacobiRotate(A, V, 0, 1);
    jacobiRotate(A, V, 0, 2);
    jacobiRotate(A, V, 1, 2);
  }
  out3[0] = A[0];
  out3[1] = A[4];
  out3[2] = A[8];
}

function normalizeColumn(M: Float64Array, col: number): number {
  const x = M[col];
  const y = M[3 + col];
  const z = M[6 + col];
  const len = Math.sqrt(x * x + y * y + z * z);
  if (len > EPS) {
    M[col] = x / len;
    M[3 + col] = y / len;
    M[6 + col] = z / len;
  }
  return len;
}

function setColumn(M: Float64Array, col: number, x: number, y: number, z: number): void {
  M[col] = x;
  M[3 + col] = y;
  M[6 + col] = z;
}

function crossColumns(M: Float64Array, a: number, b: number, out: number): void {
  const ax = M[a], ay = M[3 + a], az = M[6 + a];
  const bx = M[b], by = M[3 + b], bz = M[6 + b];
  setColumn(M, out, ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx);
}

/**
 * Computes the ARAP rotation for covariance `S` (9, row-major) into `outR` (9, row-major).
 * `outR` is guaranteed to be a proper rotation (det = +1), identity when `S` is degenerate.
 */
export function fitRotation(S: Float64Array, outR: Float64Array): void {
  // A = SᵀS  (symmetric, eigenvectors → V, eigenvalues → σ²)
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      let sum = 0;
      for (let k = 0; k < 3; k++) sum += S[3 * k + i] * S[3 * k + j];
      _A[3 * i + j] = sum;
    }
  }
  jacobiEigenSymmetric(_A, _V, _sigma);
  for (let k = 0; k < 3; k++) _sigma[k] = Math.sqrt(Math.max(_sigma[k], 0));

  // U_k = (1/σ_k) S V_k for non-degenerate σ; otherwise leave for basis completion.
  const degenerate: number[] = [];
  for (let k = 0; k < 3; k++) {
    const vx = _V[k], vy = _V[3 + k], vz = _V[6 + k]; // column k of V
    const ux = S[0] * vx + S[1] * vy + S[2] * vz;
    const uy = S[3] * vx + S[4] * vy + S[5] * vz;
    const uz = S[6] * vx + S[7] * vy + S[8] * vz;
    setColumn(_U, k, ux, uy, uz);
    if (normalizeColumn(_U, k) <= EPS) degenerate.push(k);
  }
  // Complete any degenerate U columns into an orthonormal basis.
  if (degenerate.length === 1) {
    const k = degenerate[0];
    const a = (k + 1) % 3;
    const b = (k + 2) % 3;
    crossColumns(_U, a, b, k);
    normalizeColumn(_U, k);
  } else if (degenerate.length >= 2) {
    identity(_U);
  }

  // R = V Uᵀ  →  R[i][j] = Σ_k V[i][k] U[j][k]
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      let sum = 0;
      for (let k = 0; k < 3; k++) sum += _V[3 * i + k] * _U[3 * j + k];
      outR[3 * i + j] = sum;
    }
  }

  // Determinant fix: if R is a reflection, flip the U column of the smallest σ.
  const det =
    outR[0] * (outR[4] * outR[8] - outR[5] * outR[7]) -
    outR[1] * (outR[3] * outR[8] - outR[5] * outR[6]) +
    outR[2] * (outR[3] * outR[7] - outR[4] * outR[6]);
  if (det < 0) {
    let kMin = 0;
    if (_sigma[1] < _sigma[kMin]) kMin = 1;
    if (_sigma[2] < _sigma[kMin]) kMin = 2;
    _U[kMin] = -_U[kMin];
    _U[3 + kMin] = -_U[3 + kMin];
    _U[6 + kMin] = -_U[6 + kMin];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        let sum = 0;
        for (let k = 0; k < 3; k++) sum += _V[3 * i + k] * _U[3 * j + k];
        outR[3 * i + j] = sum;
      }
    }
  }
}
